import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fmtL } from "../../../utils/currency";
import AddIcon from "@mui/icons-material/Add";
import { getAllPortfolios, getPortfolioHealthScore } from "../../../api/portfolio_api";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { MoveRightIcon } from "lucide-react";

const TABS = [
  "Buy/Sell Ratings",
  "Dividend Safety",
  "Strengths & Weaknesses",
];

const FAQS = [
  { q: "How can the stock portfolio health check help me?", color: "#8b5cf6" },
  { q: "What does the portfolio score mean?", color: "#3b82f6" },
  { q: "How often should I check my portfolio health?", color: "#10b981" },
  { q: "What makes a dividend safe or risky?", color: "#f59e0b" },
  { q: "How do I improve my portfolio score?", color: "#ec4899" },
  { q: "What is considered a good portfolio score?", color: "#06b6d4" },
  { q: "Can I check multiple portfolios at once?", color: "#8b5cf6" },
  { q: "How are the ratings calculated?", color: "#f97316" },
];

const calculatePortfolioRisk = (holdings = []) => {
  if (!holdings.length) {
    return { score: 0, grade: "N/A" };
  }

  const avg =
    holdings.reduce((sum, h) => sum + parseFloat(h.risk_score || 0), 0) /
    holdings.length;

  const score10 = avg / 10; // convert 0–100 → 0–10 scale

  let grade = "F";
  if (avg >= 90) grade = "A";
  else if (avg >= 80) grade = "B";
  else if (avg >= 70) grade = "C";
  else if (avg >= 60) grade = "D";

  return {
    score: score10,
    grade,
  };
};

const HealthCheckDialog = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: "var(--color-bg-primary)",
          backgroundImage: "none",
          borderRadius: 2,
          border: "1px solid var(--color-border-primary)",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid #334155",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)",
        }}
      >
        <Typography fontSize="var(--font-size-lg)" fontWeight={700} color="var(--color-text-primary)">
          An Instant Score And Breakdown of Your Stock Portfolio
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "var(--color-text-muted)",
            "&:hover": { bgcolor: "var(--color-surface)", color: "#e2e8f0" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* TABS */}
        <Box
          sx={{
            display: "flex",
            bgcolor: "#020617",
            borderRadius: 99,
            p: 0.5,
            mb: 3,
            border: "1px solid var(--color-border-primary)",
          }}
        >
          {TABS.map((tab, idx) => (
            <Box
              key={tab}
              onClick={() => setActiveTab(idx)}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 1,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                borderRadius: 99,
                color: activeTab === idx ? "#fff" : "#94a3b8",
                background: activeTab === idx
                  ? "var(--color-primary)"
                  : "transparent",
                transition: "all 0.2s ease",
              }}
            >
              {tab}
            </Box>
          ))}
        </Box>

        {/* SCORE + DIAGNOSIS */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={4}
          mb={4}
        >
          {/* SCORE - Enhanced Gradient Gauge */}
          <Box>
            <Typography
              fontSize="var(--font-size-xs2)"
              fontWeight={600}
              color="var(--color-text-muted)"
              mb={1}
            >
              Score
            </Typography>

            <Box
              sx={{
                width: 180,
                height: 100,
                position: "relative",
                mx: "auto",
              }}
            >
              {/* Gradient Gauge */}
              <svg width="180" height="100">
                <defs>
                  <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: "#ef4444", stopOpacity: 1 }} />
                    <stop offset="25%" style={{ stopColor: "#f59e0b", stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: "#eab308", stopOpacity: 1 }} />
                    <stop offset="75%" style={{ stopColor: "#10b981", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#06b6d4", stopOpacity: 1 }} />
                  </linearGradient>
                </defs>

                {/* Background arc */}
                <path
                  d="M20 90 A70 70 0 0 1 160 90"
                  stroke="#334155"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                />

                {/* Colored gradient arc */}
                <path
                  d="M20 90 A70 70 0 0 1 120 40"
                  stroke="url(#gaugeGradient)"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(245, 158, 11, 0.5))"
                  }}
                />
              </svg>

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Typography
                  fontSize={32}
                  fontWeight={800}
                  sx={{
                    background: "linear-gradient(135deg, #f59e0b 0%, #eab308 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {/* 4.4 */}
                </Typography>
                <Typography fontSize="var(--font-size-base)" color="var(--color-text-muted)">
                  out of 10
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* DIAGNOSIS */}
          <Box>
            <Typography
              fontSize="var(--font-size-xs2)"
              fontWeight={600}
              color="var(--color-text-muted)"
              mb={1}
            >
              Diagnosis
            </Typography>

            <Stack spacing={1.25}>
              <Box
                sx={{
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography fontSize="var(--font-size-sm)" color="#10b981">
                  ✓ Great News! You have 2 stocks rated as Ultra Safe.
                </Typography>
              </Box>

              <Box
                sx={{
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                  border: "1px solid rgba(245, 158, 11, 0.3)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography fontSize="var(--font-size-sm)" color="#f59e0b">
                  ⚠ Be Careful! One of your stocks has a risky dividend.
                </Typography>
              </Box>

              <Button
                sx={{
                  alignSelf: "flex-start",
                  mt: 1,
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  color: "#fff",
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                  },
                }}
              >
                Improve your dividend safety →
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* FAQ */}
        <Typography
          fontSize="var(--font-size-lg)"
          fontWeight={700}
          color="var(--color-text-primary)"
          mb={2}
          textAlign="center"
        >
          Frequently Asked Questions
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={2}
        >
          {FAQS.map((faq, idx) => (
            <Box
              key={idx}
              sx={{
                bgcolor: "#020617",
                border: `1px solid ${faq.color}40`,
                borderRadius: 1.5,
                px: 2,
                py: 1.75,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  borderColor: faq.color,
                  bgcolor: `${faq.color}10`,
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  bgcolor: faq.color,
                },
              }}
            >
              <Typography fontSize="var(--font-size-xs2)" color="#cbd5f5">
                {faq.q}
              </Typography>
              <AddIcon sx={{ fontSize: 16, color: faq.color }} />
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PortfolioHealthCard = ({ portfolio, healthData, loading, error }) => {
  const getScoreColor = (score) => {
    if (!score) return '#64748b';
    if (score >= 8) return '#10b981'; // Excellent
    if (score >= 6) return '#3b82f6'; // Good
    if (score >= 4) return '#f59e0b'; // Fair
    return '#ef4444'; // Poor
  };

  const getScoreGradient = (score) => {
    if (!score) return 'linear-gradient(135deg, #64748b 0%, #475569 100%)';
    if (score >= 8) return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    if (score >= 6) return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    if (score >= 4) return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  };

  const getStockGradeColor = (grade) => {
    const gradeColors = {
      'A': '#10b981',
      'B': '#3b82f6',
      'C': '#f59e0b',
      'D': '#f97316',
      'F': '#ef4444',
    };
    return gradeColors[grade] || '#64748b';
  };

  const data = healthData?.response_data;

  const { score: finalScore, grade: portfolioGrade } =
    calculatePortfolioRisk(data?.holdings);

  const navigate = useNavigate();
  return (
    <Card
      sx={{
        bgcolor: "var(--color-surface)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          borderColor: "var(--color-border-secondary)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: getScoreGradient(finalScore),
        },
      }}
    >
      <CardContent sx={{ p: 3, pt: 4 }}>
        {/* Portfolio Name */}
        <Typography fontSize="var(--font-size-base)" fontWeight={700} color="var(--color-text-primary)" mb={2}>
          {portfolio.name}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: "var(--color-error)",
              border: '1px solid rgba(239, 68, 68, 0.3)',
              '& .MuiAlert-icon': { color: "var(--color-error)" }
            }}
          >
            Failed to load health score
          </Alert>
        ) : healthData ? (
          <Box>
            {/* Score Gauge with Gradient */}
            <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <svg width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
                  <defs>
                    <linearGradient id={`gradient-${portfolio.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: getScoreColor(finalScore), stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: getScoreColor(finalScore), stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>

                  {/* Background circle */}
                  <circle
                    cx="70"
                    cy="70"
                    r="60"
                    stroke="#334155"
                    strokeWidth="12"
                    fill="none"
                  />

                  {/* Progress circle with gradient */}
                  <circle
                    cx="70"
                    cy="70"
                    r="60"
                    stroke={`url(#gradient-${portfolio.id})`}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(finalScore / 10) * 377} 377`}
                    strokeLinecap="round"
                    style={{
                      filter: `drop-shadow(0 0 8px ${getScoreColor(finalScore)}80)`,
                      transition: "stroke-dasharray 1s ease"
                    }}
                  />
                </svg>

                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    fontSize={32}
                    fontWeight={800}
                    sx={{
                      background: getScoreGradient(finalScore),
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {fmtL(finalScore, 1)}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: `${getScoreColor(finalScore)}20`,
                      border: `1px solid ${getScoreColor(finalScore)}40`,
                      borderRadius: 1,
                      px: 1,
                      py: 0.25,
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      fontSize="var(--font-size-xs2)"
                      fontWeight={700}
                      color={getScoreColor(finalScore)}
                    >
                      FA Grade: {portfolioGrade}
                    </Typography>
                  </Box>
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" mt={0.5}>
                    out of 10
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Colorful Breakdown Pie Chart */}
            {/* {data?.holdings?.length > 0 && (
              <Box mb={3}>
                <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color="var(--color-text-muted)" mb={1.5}>
                  Portfolio Composition
                </Typography>
                
                <Box display="flex" justifyContent="center" mb={2}>
                  <svg width="200" height="120" viewBox="0 0 200 120">
                    <defs>
                      {data.holdings.map((_, idx) => (
                        <linearGradient key={idx} id={`pieGradient-${idx}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" style={{ 
                            stopColor: `hsl(${(idx * 360) / data.holdings.length}, 70%, 60%)`, 
                            stopOpacity: 1 
                          }} />
                          <stop offset="100%" style={{ 
                            stopColor: `hsl(${(idx * 360) / data.holdings.length}, 70%, 45%)`, 
                            stopOpacity: 1 
                          }} />
                        </linearGradient>
                      ))}
                    </defs>
                    
                    {(() => {
                      let currentAngle = 0;
                      const total = data.holdings.length;
                      const radius = 50;
                      const cx = 100;
                      const cy = 80;
                      
                      return data.holdings.map((stock, idx) => {
                        const sliceAngle = (1 / total) * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + sliceAngle;
                        
                        const startRad = (startAngle - 90) * Math.PI / 180;
                        const endRad = (endAngle - 90) * Math.PI / 180;
                        
                        const x1 = cx + radius * Math.cos(startRad);
                        const y1 = cy + radius * Math.sin(startRad);
                        const x2 = cx + radius * Math.cos(endRad);
                        const y2 = cy + radius * Math.sin(endRad);
                        
                        const largeArc = sliceAngle > 180 ? 1 : 0;
                        
                        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                        
                        currentAngle = endAngle;
                        
                        return (
                          <path
                            key={idx}
                            d={path}
                            fill={`url(#pieGradient-${idx})`}
                            stroke="#1e293b"
                            strokeWidth="2"
                            style={{
                              filter: `drop-shadow(0 2px 4px hsl(${(idx * 360) / data.holdings.length}, 70%, 40%))`,
                            }}
                          />
                        );
                      });
                    })()}
                  </svg>
                </Box>

                <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center">
                  {data.holdings.map((stock, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        bgcolor: "var(--color-bg-primary)",
                        border: "1px solid var(--color-border-primary)",
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: `hsl(${(idx * 360) / data.holdings.length}, 70%, 55%)`,
                        }}
                      />
                      <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-secondary)">
                        {stock.ticker}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )} */}

            {/* Stock Risk Grades with Colors */}
            {data?.holdings?.length > 0 && (
              <Box mt={3}>
                <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-muted)" mb={1.5}>
                  Stock Risk Grades
                </Typography>

                <Stack spacing={1}>
                  {data.holdings.map((stock, idx) => (
                    <Box
                      key={stock.id}
                      sx={{
                        bgcolor: "var(--color-bg-primary)",
                        border: `1px solid ${getStockGradeColor(stock.risk_grade)}40`,
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          bgcolor: getStockGradeColor(stock.risk_grade),
                        },
                      }}
                    >
                      <Typography
                        fontSize="var(--font-size-sm)"
                        fontWeight={600}
                        className="text-blue-400 hover:text-blue-300 font-bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`/stocks/${stock.ticker}/details`)
                        }>
                        {stock.ticker}
                      </Typography>

                      <Box
                        sx={{
                          bgcolor: `${getStockGradeColor(stock.risk_grade)}20`,
                          border: `1px solid ${getStockGradeColor(stock.risk_grade)}40`,
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          fontSize="var(--font-size-sm)"
                          fontWeight={600}
                          color={getStockGradeColor(stock.risk_grade)}
                        >
                          {stock.risk_grade}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Recommendations with Colors */}
            {healthData.recommendations && healthData.recommendations.length > 0 && (
              <Box mt={3}>
                <Typography fontSize="var(--font-size-xs2)" fontWeight={600} color="var(--color-text-muted)" mb={1.5}>
                  Recommendations
                </Typography>
                <Stack spacing={1}>
                  {healthData.recommendations.slice(0, 2).map((rec, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        bgcolor: "var(--color-bg-primary)",
                        border: `1px solid ${idx === 0 ? '#3b82f6' : '#8b5cf6'}40`,
                        borderRadius: 1,
                        p: 1.5,
                        borderLeft: `3px solid ${idx === 0 ? '#3b82f6' : '#8b5cf6'}`,
                      }}
                    >
                      <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-secondary)">
                        {rec}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ) : (
          <Typography fontSize="var(--font-size-xs2)" color="var(--color-text-muted)" textAlign="center" py={4}>
            No health data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const PortfolioHealthCheck = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [healthScores, setHealthScores] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchingPortfolios, setFetchingPortfolios] = useState(true);
  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    if (location.state?.openGuide) {
      setDialogOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    fetchPortfolios();
  }, []);

  const fetchPortfolios = async () => {
    setFetchingPortfolios(true);
    try {
      const res = await getAllPortfolios();
      if (res?.success && res.data) {
        setPortfolios(res.data);
        res.data.forEach(portfolio => {
          fetchHealthScore(portfolio.name, portfolio.id);
        });
      }
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    } finally {
      setFetchingPortfolios(false);
    }
  };

  const fetchHealthScore = async (portfolioName, portfolioId) => {
    setLoading(prev => ({ ...prev, [portfolioId]: true }));
    setErrors(prev => ({ ...prev, [portfolioId]: null }));

    try {
      const res = await getPortfolioHealthScore(portfolioName);
      if (res?.success && res.data) {
        setHealthScores(prev => ({ ...prev, [portfolioId]: res.data }));
      } else {
        setErrors(prev => ({ ...prev, [portfolioId]: 'Failed to load' }));
      }
    } catch (error) {
      console.error(`Error fetching health score for ${portfolioName}:`, error);
      setErrors(prev => ({ ...prev, [portfolioId]: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, [portfolioId]: false }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "var(--color-bg-primary)",
        px: { xs: 2, md: 3 },
        py: 2.5,
      }}
    >
      <HealthCheckDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

      {/* Page Header with Gradient */}
      <Box
        mb={3}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box style={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant='title'
              fontWeight="var(--font-weight-bold)"
              className="text-primary"
              sx={{ color: 'var(--color-text-primary)' }}
              mb={0.25}
              letterSpacing="-0.01em"
            >
              Portfolio Health Check
            </Typography>
            <Typography
              variant='subtitle'
              sx={{ color: 'var(--color-text-secondary)' }}
              fontWeight={400}
            >
              Review the health scores and recommendations for all your portfolios
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            {/* <Button
              startIcon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
              onClick={fetchPortfolios}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: 1.5,
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Run Risk Analysis
            </Button> */}
            <Button
              endIcon={<MoveRightIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/allPortfolio")}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                fontSize: "var(--font-size-sm)",
                fontWeight:"var(--font-weight-semibold)",
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: 1.5,
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              View all Portfolio
            </Button>

            <Button
              onClick={() => setDialogOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                color: "#fff",
                fontSize: "var(--font-size-sm)",
                fontWeight:"var(--font-weight-semibold)",
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: 1.5,
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.5)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              View Guide
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Loading State */}
      {fetchingPortfolios ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={10}>
          <Box textAlign="center">
            <CircularProgress
              size={48}
              sx={{
                color: '#3b82f6',
                mb: 2,
                filter: "drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))"
              }}
            />
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
              Loading portfolios...
            </Typography>
          </Box>
        </Box>
      ) : portfolios.length === 0 ? (
        <Box
          sx={{
            bgcolor: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 2,
            p: 6,
            textAlign: "center",
          }}
        >
          <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)" mb={1}>
            No Portfolios Found
          </Typography>
          <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" mb={3}>
            Create a portfolio to start tracking your investments
          </Typography>
          <Button
            onClick={() => navigate('/allPortfolio')}
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              py: 1.5,
              borderRadius: 1.5,
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(59, 130, 246, 0.5)",
              },
            }}
          >
            Create Portfolio
          </Button>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          }}
          gap={3}
        >
          {portfolios.map((portfolio) => (
            <PortfolioHealthCard
              key={portfolio.id}
              portfolio={portfolio}
              healthData={healthScores[portfolio.id]}
              loading={loading[portfolio.id]}
              error={errors[portfolio.id]}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PortfolioHealthCheck;